"use client";

import * as React from "react";
import { useLocation } from "wouter";

// Type declarations for Web Speech API
interface SpeechRecognitionResult {
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  readonly isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEventType extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEventType extends Event {
  readonly error: string;
  readonly message: string;
}

interface SpeechRecognitionType extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: ((this: SpeechRecognitionType, ev: Event) => void) | null;
  onresult: ((this: SpeechRecognitionType, ev: SpeechRecognitionEventType) => void) | null;
  onerror: ((this: SpeechRecognitionType, ev: SpeechRecognitionErrorEventType) => void) | null;
  onend: ((this: SpeechRecognitionType, ev: Event) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionType;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

interface MobileNavigationContextValue {
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  toggleMenu: () => void;
  isSearchOpen: boolean;
  setIsSearchOpen: (open: boolean) => void;
  toggleSearch: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isVoiceSearchActive: boolean;
  setIsVoiceSearchActive: (active: boolean) => void;
  startVoiceSearch: () => void;
  stopVoiceSearch: () => void;
  navigationHistory: string[];
  canGoBack: boolean;
  goBack: () => void;
  currentPath: string;
}

const MobileNavigationContext = React.createContext<MobileNavigationContextValue | undefined>(undefined);

export function useMobileNavigation() {
  const context = React.useContext(MobileNavigationContext);
  if (!context) {
    throw new Error("useMobileNavigation must be used within a MobileNavigationProvider");
  }
  return context;
}

interface MobileNavigationProviderProps {
  children: React.ReactNode;
}

export function MobileNavigationProvider({ children }: MobileNavigationProviderProps) {
  const [location, setLocation] = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isVoiceSearchActive, setIsVoiceSearchActive] = React.useState(false);
  const [navigationHistory, setNavigationHistory] = React.useState<string[]>([]);
  const recognitionRef = React.useRef<SpeechRecognitionType | null>(null);

  // Track navigation history
  React.useEffect(() => {
    setNavigationHistory((prev) => {
      if (prev[prev.length - 1] !== location) {
        return [...prev, location];
      }
      return prev;
    });
  }, [location]);

  // Close menu on route change
  React.useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  React.useEffect(() => {
    if (isMenuOpen || isSearchOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen, isSearchOpen]);

  const toggleMenu = React.useCallback(() => {
    setIsMenuOpen((prev) => !prev);
    if (isSearchOpen) setIsSearchOpen(false);
  }, [isSearchOpen]);

  const toggleSearch = React.useCallback(() => {
    setIsSearchOpen((prev) => !prev);
    if (isMenuOpen) setIsMenuOpen(false);
  }, [isMenuOpen]);

  const canGoBack = navigationHistory.length > 1;

  const goBack = React.useCallback(() => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current
      const previousPath = newHistory[newHistory.length - 1] || "/";
      setNavigationHistory(newHistory);
      setLocation(previousPath);
    }
  }, [navigationHistory, setLocation]);

  const startVoiceSearch = React.useCallback(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) {
      console.warn("Voice search is not supported in this browser");
      return;
    }

    const recognition = new SpeechRecognitionAPI();

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsVoiceSearchActive(true);
    };

    recognition.onresult = (event: SpeechRecognitionEventType) => {
      const results: string[] = [];
      for (let i = 0; i < event.results.length; i++) {
        results.push(event.results[i][0].transcript);
      }
      setSearchQuery(results.join(""));
    };

    recognition.onerror = (event: SpeechRecognitionErrorEventType) => {
      console.error("Voice search error:", event.error);
      setIsVoiceSearchActive(false);
    };

    recognition.onend = () => {
      setIsVoiceSearchActive(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, []);

  const stopVoiceSearch = React.useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsVoiceSearchActive(false);
    }
  }, []);

  const value = React.useMemo(
    () => ({
      isMenuOpen,
      setIsMenuOpen,
      toggleMenu,
      isSearchOpen,
      setIsSearchOpen,
      toggleSearch,
      searchQuery,
      setSearchQuery,
      isVoiceSearchActive,
      setIsVoiceSearchActive,
      startVoiceSearch,
      stopVoiceSearch,
      navigationHistory,
      canGoBack,
      goBack,
      currentPath: location,
    }),
    [
      isMenuOpen,
      toggleMenu,
      isSearchOpen,
      toggleSearch,
      searchQuery,
      isVoiceSearchActive,
      startVoiceSearch,
      stopVoiceSearch,
      navigationHistory,
      canGoBack,
      goBack,
      location,
    ]
  );

  return (
    <MobileNavigationContext.Provider value={value}>
      {children}
    </MobileNavigationContext.Provider>
  );
}
